/* eslint-disable @typescript-eslint/no-explicit-any -- any is useful for generic interfaces */

import { Draft } from "immer";

import { AppSessionEvent } from "./AppSessionEvent.js";
import { AppSessionEventState } from "./AppSessionEventState.js";

/**
 * Actual definition of a specific app session event.
 *
 * Contains metadata and helper for the event, such as how to update the state,
 * how to safely log the event, etc
 */
export interface AppSessionEventDefinition<
  EVENT extends AppSessionEvent<string, any>,
  CREATE_ARGS extends any[],
> {
  /**
   * Used as the discriminant for the event itself
   */
  type: EVENT["type"];

  /**
   * Which keys of type `PAYLOAD` whose values are safe to log.
   *
   * Can also be a function that takes an event and produces a record of log safe values.
   */
  logSafe:
    | Record<keyof EVENT["payload"], boolean>
    | ((args: {
        event: EVENT;
        dangerouslyUnsafe: boolean;
      }) => Record<string, unknown>);

  /**
   * Helper for creating events of this type.
   */
  create: (...args: CREATE_ARGS) => EVENT;

  /**
   * How to actually update the state once this event has been received.
   */
  apply: (
    previousState: Draft<AppSessionEventState>,
    payload: EVENT["payload"],
  ) => void | { warnings: string[] };
}

/**
 * Helper function that makes it easier to define app session events
 */
export const createAppSessionEventDefinition = <
  TYPE extends string,
  PAYLOAD extends Record<string, any>,
  CREATE_ARGS extends any[],
>({
  apply,
  create,
  logSafe,
  type,
}: {
  type: TYPE;
  logSafe: AppSessionEventDefinition<
    AppSessionEvent<TYPE, PAYLOAD>,
    CREATE_ARGS
  >["logSafe"];
  create: (...args: CREATE_ARGS) => PAYLOAD;
  apply: (
    previousState: Draft<AppSessionEventState>,
    payload: PAYLOAD,
  ) => void | { warnings: string[] };
}): AppSessionEventDefinition<AppSessionEvent<TYPE, PAYLOAD>, CREATE_ARGS> => ({
  type,
  logSafe,
  create: (...args: CREATE_ARGS) => ({ type, payload: create(...args) }),
  apply,
});
